// React-Query hooks для /profile/me (Sprint 2 A-1, A-2).
// Source of truth: docs/specs/sprint-3/stage-1-contracts.md §8.1.
//
// ProfileMeResponse — самодостаточный source для onboarding state + resume-step
// (см. §3.4 stage-1: discriminated unions с embed'нутым survey + consent).
// Дополнительный GET /survey в Sprint 3 не нужен.

import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type {
  ApiError,
  PatchProfileRequest,
  PatchProfileResponse,
  ProfileMeResponse,
} from '@/api/types';

export function useProfileMe(enabled = true): UseQueryResult<ProfileMeResponse, AxiosError<ApiError>> {
  return useQuery({
    queryKey: queryKeys.profileMe,
    queryFn: async () => {
      const { data } = await api.get<ProfileMeResponse>('/profile/me');
      return data;
    },
    enabled,
    // HEAL-76: возврат приложения из фона обновляет счётчик уведомлений.
    //
    // Глобально refetchOnWindowFocus выключен (main.tsx), поэтому включаем
    // точечно — само по себе появление поля meta.unread_notifications счётчик
    // бы не обновляло. Механика уже есть: lib/app-focus.ts с 46b подключает
    // состояние приложения Capacitor к менеджеру фокуса React Query именно с
    // расчётом на то, что дальше каждый запрос решает сам.
    refetchOnWindowFocus: true,
  });
}

/**
 * HEAL-43 — удаление аккаунта. Инвалидации кэша здесь нет намеренно: после успеха
 * пользователя больше нет, и любой refetch упрётся в 401. Экран сам чистит кэш и
 * локальный токен и уводит на вход.
 */
export function useDeleteAccount(): UseMutationResult<
  { message: string },
  AxiosError<ApiError>,
  { password: string }
> {
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.delete<{ message: string }>('/profile/me', { data: body });
      return data;
    },
  });
}

export function usePatchProfile(): UseMutationResult<PatchProfileResponse, AxiosError<ApiError>, PatchProfileRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api.patch<PatchProfileResponse>('/profile/me', body);
      return data;
    },
    onSuccess: () => {
      // Auto-invalidate (см. stage-4 §2.2): токен уже актуален, race не грозит.
      void qc.invalidateQueries({ queryKey: queryKeys.profileMe });
      void qc.invalidateQueries({ queryKey: queryKeys.authMe });
    },
  });
}
